import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import axiosInstance from "../../services/axiosInstance";
import { FaLongArrowAltRight, FaLongArrowAltLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const FormSchema = z.object({
  sign_up_reason: z.enum(
    [
      "Youtube",
      "LinkedIn",
      "Google Search",
      "Reddit",
      "Email Newsletter",
      "Referral from a Friend",
      "Word of Mouth",
      "Other",
    ],
    {
      required_error: "You must select an option to let us know how you heard about us.",
    }
  ),
});

function HowDidYouHearForm({ onStepComplete, onStepPrevious }) {
  const [pending, setPending] = useState(false);
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: { sign_up_reason: undefined },
  });

  async function onSubmit(data) {
    console.log("data:",data);
    setPending(true);
    try {
      const instance = axiosInstance();
      await instance.post("/user/add_sign_up_reason", data);
      toast.success("Onboarding complete! Thank you for joining us!");
      if (onStepComplete) onStepComplete();
      navigate("/home");
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setPending(false);
    }
  }

  return (
    <Card className="w-full max-w-4xl bg-white text-black shadow-md rounded-lg">
      <CardContent className="p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <h1 className="text-2xl font-semibold mb-2">
              How Did You Hear About Us?*
            </h1>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Controller
                name="sign_up_reason"
                control={control}
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="space-y-3"
                  >
                    {[
                      "Youtube",
                      "LinkedIn",
                      "Google Search",
                      "Reddit",
                      "Email Newsletter",
                      "Referral from a Friend",
                      "Word of Mouth",
                      "Other",
                    ].map((option) => (
                      <div key={option} className="flex items-center space-x-3">
                        <RadioGroupItem
                          value={option}
                          className={
                            errors.sign_up_reason ? "ring-1 ring-red-500" : ""
                          }
                        />
                        <Label className="font-normal capitalize">{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
              />
              {errors.sign_up_reason && (
                <span className="text-red-500 text-sm">
                  {errors.sign_up_reason.message}
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-between">
            <Button
              type="button"
              onClick={onStepPrevious}
              className="flex items-center"
            >
            <FaLongArrowAltLeft className="mr-2" />
              
              Previous
            </Button>
            <Button type="submit" disabled={pending} className="flex items-center">
              {pending ? "Submitting..." : "Submit"}
            <FaLongArrowAltRight className="ml-2" />
              
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export default HowDidYouHearForm;
